import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { CommonsService } from '@ruby/core/http/commons/commons.service';
import { ThirdPartyService } from '@ruby/core/http/third-party/third-party.service';
import { IThirdPartyEcrEquipmentTypes, IThirdPartyLineOfBusiness } from '@ruby/modules/third-party/models/third-party.interface';
import * as ThirdPartyAuthorizationActions from '@ruby/modules/third-party/store/actions/third-party-authorization.actions';
import { IStation, IStationDetail } from '@ruby/shared/models/commons/station.interface';
import { IError } from '@ruby/shared/models/request/error.interface';

@Injectable()
export class ThirdPartyAuthorizationEffects {
  getAvailableLineOfBusiness$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyAuthorizationActions.loadAvailableLineOfBusiness),
    exhaustMap(action => this.thirdPartySvc.getAvailableLineOfBusiness(action.companyEId, action.tplCompanyEId).pipe(
      map((lob: Array<IThirdPartyLineOfBusiness>) =>
        ThirdPartyAuthorizationActions.loadAvailableLineOfBusinessSuccess({ data: lob, afterSign: action.afterSign })),
      catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadAvailableLineOfBusinessFailure({ error })))
    ))
  ));

  getEquipmentTypes$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyAuthorizationActions.loadEquipmentTypes),
    exhaustMap(() => this.thirdPartySvc.getEquipmentTypes().pipe(
      map((equipments: Array<IThirdPartyEcrEquipmentTypes>) => ThirdPartyAuthorizationActions.loadEquipmentTypesSuccess({ data: equipments })),
      catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadEquipmentTypesFailure({ error })))
    ))
  ));

  getStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyAuthorizationActions.loadStates),
      exhaustMap(() =>
        this.commonSvc.fetchStatesV2().pipe(
          map((response: Array<INSAutoCompleteGroup>) => ThirdPartyAuthorizationActions.loadStatesSuccess({ data: [...response].reverse() })),
          catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadStatesFailure({ error })))
        )
      )
    )
  );

  getDestinationStation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyAuthorizationActions.loadDestinationStations),
      exhaustMap(({ data }: { data: IStation }) =>
        this.commonSvc.fetchCities(data).pipe(
          map((response: Array<IStationDetail>) =>
            ThirdPartyAuthorizationActions.loadDestinationStationsSuccess({ data: response })
          ),
          catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadDestinationStationsFailure({ error })))
        )
      )
    )
  );

  getStcc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyAuthorizationActions.loadStcc),
      exhaustMap(action =>
        this.commonSvc.fetchStcc(action.data).pipe(
          map((response: Array<INSAutoCompleteOption>) => ThirdPartyAuthorizationActions.loadStccSuccess({ data: response })),
          catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadStccFailure({ error })))
        )
      )
    )
  );

  getScac$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyAuthorizationActions.loadScac),
      exhaustMap(action =>
        this.thirdPartySvc.getScac(action.data).pipe(
          map((response: Array<INSAutoCompleteOption>) => ThirdPartyAuthorizationActions.loadScacSuccess({ data: response })),
          catchError((error: IError) => of(ThirdPartyAuthorizationActions.loadScacFailure({ error })))
        )
      )
    )
  );

  constructor(private actions$: Actions, private thirdPartySvc: ThirdPartyService, private commonSvc: CommonsService) {}
}
